import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Stack, Avatar,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { meApi } from '../../api';
import { useRoleProfile } from '../../hooks/useRoleProfile';
import { fmtLocalTime } from '../../utils/datetime';
import { useTranslation } from 'react-i18next';

type Period = 'today' | 'week' | 'month';

interface TeamSummaryStat { worker_id: string; name: string; count: number }

interface TeamSummary {
  period: Period;
  headcount: number;
  present: number;
  late: number;
  absent: number;
  not_in: number;
  present_rate: number;
  top_late: TeamSummaryStat[];
  top_absent: TeamSummaryStat[];
}

interface TeamMember {
  id: string;
  worker_code: string;
  name: string;
  first_name: string;
  last_name: string;
  position?: string | null;
  avatar_url?: string | null;
  email?: string | null;
  today_clock_in?: string | null;
  today_clock_out?: string | null;
  today_status?: string | null;
}

function statusLabel(m: TeamMember): { key: string; color: 'success' | 'warning' | 'default' | 'info' } {
  if (!m.today_clock_in) return { key: 'statusNotClockedIn', color: 'default' };
  if (m.today_clock_out) return { key: 'statusDayComplete', color: 'info' };
  if (m.today_status === 'late') return { key: 'statusClockedInLate', color: 'warning' };
  return { key: 'statusClockedIn', color: 'success' };
}

/** GET /v1/me/process-summary. `scope` is what the render gates on — the SERVER decides the tier, and
 *  re-deriving it from the role would be a second source of truth for one decision. */
interface ProcessSummary {
  scope: string;          // organization | team | self
  period: string;
  total: number;
  running: number;
  completed: number;
  rejected: number;
  pending: number;
  completion_rate: number;
}

function initials(name: string): string {
  return name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}

const WorkerTeam: React.FC = () => {
  const { t } = useTranslation();
  /**
   * THE LICENCE FOLLOWS THE CONTENT, not the page (2026-09-04).
   *
   * `/me/team` is declared product-free in `src/auth/workerSurfaceLicence.ts` because the roster is
   * EMPLOYMENT data — who reports to me, their position, how to reach them — which the backend serves
   * to any licence and whose own docstring says so. Gating the whole page on QuTime kept a PureOA-only
   * manager away from a roster the API would have given them.
   *
   * The ATTENDANCE parts of the page are a different matter: today's clock-in/out/status columns and
   * the summary strip are QuTime, and for a PureOA-only org they would be three permanently empty
   * columns — which is worse than absent, because an empty column invites the question "why".
   */
  const { role } = useRoleProfile();
  const hasQutime = role?.hasQutime ?? false;
  const hasPureoa = role?.hasPureoa ?? false;
  const [period, setPeriod] = useState<Period>('month');
  const [summary, setSummary] = useState<TeamSummary | null>(null);
  const [proc, setProc] = useState<ProcessSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (await meApi.team.list()) as TeamMember[];
      setTeam(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common:feedback.loadFailed');
      setError(msg);
      setTeam([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * The aggregate, from `/me/team-summary`. ADDED 2026-09-04 after an audit found the web manager had
   * no month view on any screen while the phone has had `team-report` since it shipped — so "is this
   * person repeatedly late" was answerable on a phone and nowhere at a desk.
   *
   * `month` is the DEFAULT, not `today`: the roster below already answers today per person, so the
   * aggregate earns its place by answering the question the roster cannot.
   *
   * Deliberately NOT a second page. `/me/team-today` exists as an endpoint, but this roster already
   * IS its per-person half — adding a screen for it would be a second answer to a question already
   * answered (svdy-rules §10.5b).
   */
  useEffect(() => {
    if (!hasQutime) { setSummary(null); return; }
    let live = true;
    meApi.team.summary(period)
      .then((r) => { if (live) setSummary((r as { data: TeamSummary }).data); })
      .catch(() => { if (live) setSummary(null); });   // the roster is the page; the strip is extra
    return () => { live = false; };
  }, [period, hasQutime]);

  /**
   * The PureOA half, added 2026-09-05 after an independent market audit of the owner's question "the
   * mobile Team tab has attendance and process reports; the web portal has neither".
   *
   * Half of that was already false — the attendance strip above IS the web's report, inline, because a
   * desktop can hold the roster and the aggregate at once where a phone cannot. What was genuinely
   * missing is this: a PureOA-only manager had no oversight aggregate at all, while a QuTime-only
   * manager had a full one, in a product that sells the two lines as equals.
   *
   * MEASURED RATHER THAN ASSUMED, across 10 comparable SMB products using permission matrices and
   * role-comparison pages only:
   *   - a non-admin manager gets a WEB reporting surface in 6 of 8 verifiable products;
   *   - "mobile has manager reporting the web lacks" is an ANOMALY, and the direction is reversed —
   *     0 of 4, with Factorial stating outright that no manager-specific attendance screens exist on
   *     mobile at all;
   *   - and 0 of 10 ship a PERSONAL throughput report, which is what this endpoint used to hand a
   *     manager. The market makes personal surfaces LISTS and balances, and puts completion rate at
   *     team or process level (Zoho People's IA is literally My / Team / Organization Reports).
   *
   * So the backend gained a `team` scope rather than the web gaining a personal ring. Rendered only at
   * `team` or `organization` scope: at `self` this is one person's own completion rate, which is the
   * shape nobody ships and which the requests list already answers per-request.
   */
  useEffect(() => {
    if (!hasPureoa) { setProc(null); return; }
    let live = true;
    meApi.team.processSummary(period)
      .then((r) => { if (live) setProc((r as { data: ProcessSummary }).data); })
      .catch(() => { if (live) setProc(null); });
    return () => { live = false; };
  }, [period, hasPureoa]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ mb: 1.5, width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('worker:team.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('worker:team.teamSubtitle')}</Typography>
        </Box>
        {/* The window of truth belongs in the header row, not the filter bar (svdy-ui §4) — and it is
            an ATTENDANCE window, so it goes with the columns it governs. */}
        {hasQutime ? (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={period}
          onChange={(_, v) => { if (v) setPeriod(v as Period); }}
          sx={{ flexShrink: 0, '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1.25, borderRadius: 1 } }}
        >
          <ToggleButton value="today">{t('worker:team.periodToday')}</ToggleButton>
          <ToggleButton value="week">{t('worker:team.periodWeek')}</ToggleButton>
          <ToggleButton value="month">{t('worker:team.periodMonth')}</ToggleButton>
        </ToggleButtonGroup>
        ) : null}
      </Box>

      {/* §3 inline stats — one muted line with bold numbers, never a grid of stat cards. `today`
          counts PEOPLE; `week`/`month` count PERSON-DAYS, which is why the labels differ per period
          rather than reusing one word for two units.

          `headcount > 0` ADDED 2026-09-07. `headcount` counts attendance-TRACKED reports and excludes
          self, so it is legitimately zero in two reachable cases — a brand-new QuTime tenant whose only
          worker is the owner, and a manager whose reports do not punch (which since the same day can
          reach this page, because oversight is now an employment fact). Without the term the strip read
          `0 people · 0% present rate · 0 late · 0 not in`: four zeros presented as measurements. The
          mobile twin hides its ring in the same case and shows the empty-board sentence instead. */}
      {summary && summary.headcount > 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          <strong>{summary.headcount}</strong> {t('worker:team.statPeople')}
          {' · '}
          <strong>{Math.round(summary.present_rate)}%</strong> {t('worker:team.statPresentRate')}
          {' · '}
          <strong>{summary.late}</strong> {period === 'today' ? t('worker:team.statLatePeople') : t('worker:team.statLateDays')}
          {' · '}
          <strong>{period === 'today' ? summary.not_in : summary.absent}</strong>{' '}
          {period === 'today' ? t('worker:team.statNotIn') : t('worker:team.statAbsentDays')}
        </Typography>
      ) : null}

      {/* The PureOA line, in the SAME §3 inline-stats shape as attendance above rather than a card or a
          donut. Two aggregates on one page must look like one page (svdy-ui §15: one alignment axis).

          `scope !== 'self'` is the gate. At `self` these are the viewer's OWN requests — a personal
          completion rate, which 0 of 10 comparable SMB products ship and which the requests list already
          answers per-request. `pending` is the current open count (all-time), not period-windowed, so it
          is labelled separately rather than folded into the period figures. */}
      {proc && proc.scope !== 'self' ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          <strong>{proc.total}</strong> {t('worker:team.statRequests')}
          {' · '}
          <strong>{Math.round(proc.completion_rate)}%</strong> {t('worker:team.statCompleted')}
          {' · '}
          <strong>{proc.running}</strong> {t('worker:team.statInProgress')}
          {' · '}
          <strong>{proc.pending}</strong> {t('worker:team.statOpenNow')}
        </Typography>
      ) : null}

      {/* Repeat offenders, and ONLY when there are any — this is the answer to "is it a recurring
          problem with one person", which no per-person dialog needs to duplicate. */}
      {summary && period !== 'today' && (summary.top_late.length > 0 || summary.top_absent.length > 0) ? (
        <Stack direction="row" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap' }} useFlexGap>
          {summary.top_late.length > 0 ? (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }} useFlexGap>
              <Typography variant="caption" color="text.secondary">{t('worker:team.repeatLate')}</Typography>
              {summary.top_late.map((w) => (
                <Chip key={w.worker_id} size="small" variant="outlined" color="warning"
                  label={`${w.name} · ${w.count}`}
                  sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }} />
              ))}
            </Stack>
          ) : null}
          {summary.top_absent.length > 0 ? (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }} useFlexGap>
              <Typography variant="caption" color="text.secondary">{t('worker:team.repeatAbsent')}</Typography>
              {summary.top_absent.map((w) => (
                <Chip key={w.worker_id} size="small" variant="outlined" color="error"
                  label={`${w.name} · ${w.count}`}
                  sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }} />
              ))}
            </Stack>
          ) : null}
        </Stack>
      ) : null}

      {error ? (
        <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'error.light', bgcolor: 'error.lighter' }}>
          <Typography variant="body2" color="error.main">{error}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('worker:team.loadErrorHint')}
          </Typography>
        </Paper>
      ) : team.length === 0 ? (
        <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">{t('worker:team.noReports')}</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {t('worker:team.noReportsHint')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600, fontSize: '0.75rem' } }}>
              <TableRow>
                <TableCell>{t('worker:team.colName')}</TableCell>
                <TableCell>{t('worker:team.colPosition')}</TableCell>
                {/* Contact is EMPLOYMENT data, so it is here in every licence. Until 2026-09-05 this
                    column lived in the `else` branch below and appeared only when QuTime was ABSENT —
                    the comment there said the roster "keeps a contact column instead", and `instead`
                    was the tell: it was filler for the space the punch columns left, not a decision.
                    The effect was that a manager in a QuTime org could not see a report's email while
                    a manager in a PureOA org could, on the same screen, decided by an unrelated
                    licence. Each column is now derived from what it IS (svdy-ui §20). */}
                <TableCell>{t('worker:team.labelEmail')}</TableCell>
                {hasQutime && (
                  <>
                    <TableCell>{t('worker:team.colClockIn')}</TableCell>
                    <TableCell>{t('worker:team.colClockOut')}</TableCell>
                    <TableCell align="center">{t('worker:team.colToday')}</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {team.map((m) => {
                const s = statusLabel(m);
                const timeIn = fmtLocalTime(m.today_clock_in);
                const timeOut = fmtLocalTime(m.today_clock_out);
                return (
                  <TableRow key={m.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar src={m.avatar_url ?? undefined} sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                          {initials(m.name)}
                        </Avatar>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>{m.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell><Typography variant="caption">{m.position ?? '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{m.email ?? '—'}</Typography></TableCell>
                    {hasQutime && (
                      <>
                        <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{timeIn ?? '—'}</Typography></TableCell>
                        <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{timeOut ?? '—'}</Typography></TableCell>
                        <TableCell align="center">
                          <Chip
                            label={t(`worker:team.${s.key}`)}
                            size="small"
                            color={s.color}
                            variant="outlined"
                            sx={{ borderRadius: 1, height: 18, fontSize: '0.65rem' }}
                          />
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* THE DETAIL DIALOG WAS REMOVED 2026-09-05, and the measurement is why rather than taste.
          It held five rows. Three repeated the row that had just been clicked, and once contact moved
          into the table above (it had been conditional on the ABSENCE of a QuTime licence), its only
          remaining net-new field in EITHER licence was Employee ID. Under PureOA it was already a
          one-field dialog.

          Employee ID does not belong on a team lead's roster. It is the IMPORT MATCHING KEY, so its
          readers are admins, and it is in the admin shell already (`Organization/ActiveWorkers`, 12
          sites). A worker's own code is on their own profile, where they may need to quote it. No use
          could be constructed for a manager needing a REPORT's code, and the mobile twin has no such
          surface at all — `app/team-member/[id].tsx` is punch detail, a different thing.

          The row is consequently no longer clickable, which is also the correct end state: its cells
          mean different things, so a single row-level action over them was the svdy-ui §12.8 defect
          waiting to be reported. */}
    </Box>
  );
};


export default WorkerTeam;
